import React, { useState } from 'react';
import {
    AppBar,
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Container,
    Grid,
    IconButton,
    Paper,
    Typography,
    Toolbar,
    Avatar,
    Stack,
} from '@mui/material';
import {
    Favorite as FavoriteIcon,
    FavoriteBorder as FavoriteBorderIcon,
    ChatBubbleOutline as CommentIcon,
    Share as ShareIcon,
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
    ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import SideMenuNote from './SideMenuNote';

const initialPosts = {
    trending: [
        {
            id: 1,
            title: "React開発の基礎",
            excerpt: "Reactを使った開発の基本的な流れについて解説します。",
            content: "Reactは、Facebookが開発したユーザーインターフェース構築のためのJavaScriptライブラリです...",
            author: {
                id: 1,
                name: "テック太郎",
                avatar: "/api/placeholder/40/40"
            },
            likes: 42,
            isLiked: false,
            comments: 5,
            createdAt: "2024-01-15",
            coverImage: "/api/placeholder/800/400",
            category: "トレンド"
        },
        {
            id: 2,
            title: "プログラミング初心者のための学習ロードマップ",
            excerpt: "プログラミングを始めたい方向けの、段階的な学習計画を紹介します。",
            content: "プログラミングを始めるときは、まず基礎的な概念から学んでいくことが重要です...",
            author: {
                id: 2,
                name: "レッスン花子",
                avatar: "/api/placeholder/40/40"
            },
            likes: 28,
            isLiked: false,
            comments: 3,
            createdAt: "2024-01-14",
            coverImage: "/api/placeholder/800/400",
            category: "学習"
        }
    ],
    programming: [
        {
            id: 3,
            title: "モダンJavaScriptの新機能",
            excerpt: "ES2024で導入される新機能について詳しく解説します。",
            content: "JavaScriptの新しい機能について...",
            author: {
                id: 3,
                name: "JS太郎",
                avatar: "/api/placeholder/40/40"
            },
            likes: 35,
            isLiked: false,
            comments: 4,
            createdAt: "2024-01-13",
            coverImage: "/api/placeholder/800/400",
            category: "プログラミング"
        }
    ]
};

const topics = {
    trending: 'トレンド',
    programming: 'プログラミング',
    design: 'デザイン'
};

const PostCard = ({ post, onSelectPost, onToggleLike, isHorizontal = false }) => {
    const theme = useTheme();
    const handleLike = (e) => {
        e.stopPropagation();
        onToggleLike(post.id);
    };

    const cardStyles = isHorizontal ? {
        width: 280,
        flexShrink: 0
    } : {
        height: '100%'
    };

    return (
        <Card
            sx={{
                ...cardStyles,
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: theme.shadows[4]
                },
                transition: 'box-shadow 0.2s'
            }}
            onClick={() => onSelectPost(post)}
        >
            <CardMedia
                component="img"
                height={160}
                image={post.coverImage}
                alt={post.title}
            />
            <CardContent>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        <Avatar
                            src={post.author.avatar}
                            alt={post.author.name}
                            sx={{ width: 32, height: 32 }}
                        />
                        <Box>
                            <Typography variant="subtitle1" noWrap sx={{ mb: 0.5 }}>
                                {post.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {post.author.name}
                            </Typography>
                        </Box>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        {post.excerpt}
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <IconButton size="small" onClick={handleLike}>
                            {post.isLiked ? (
                                <FavoriteIcon fontSize="small" color="error" />
                            ) : (
                                <FavoriteBorderIcon fontSize="small" />
                            )}
                        </IconButton>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {post.likes}
                        </Typography>
                        <IconButton size="small">
                            <CommentIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {post.comments}
                        </Typography>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
};

const TopicSection = ({ title, posts, onSelectPost, onToggleLike, onViewMore }) => (
    <Box sx={{ mb: 6 }}>
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 3 }}
        >
            <Typography variant="h5" component="h2" fontWeight="bold">
                {title}
            </Typography>
            <Button
                endIcon={<ChevronRightIcon />}
                onClick={onViewMore}
            >
                もっと見る
            </Button>
        </Stack>
        <Box
            sx={{
                display: 'flex',
                gap: 3,
                overflowX: 'auto',
                pb: 2,
                '::-webkit-scrollbar': {
                    height: 6
                },
                '::-webkit-scrollbar-track': {
                    backgroundColor: 'grey.100'
                },
                '::-webkit-scrollbar-thumb': {
                    backgroundColor: 'grey.400',
                    borderRadius: 3
                }
            }}
        >
            {posts.map(post => (
                <PostCard
                    key={post.id}
                    post={post}
                    onSelectPost={onSelectPost}
                    onToggleLike={onToggleLike}
                    isHorizontal
                />
            ))}
        </Box>
    </Box>
);

const CategoryPage = ({ category, posts, onSelectPost, onToggleLike, onBack }) => (
    <Box>
        <Stack spacing={2} sx={{ mb: 4 }}>
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={onBack}
                sx={{ alignSelf: 'flex-start' }}
            >
                戻る
            </Button>
            <Typography variant="h4" component="h1" fontWeight="bold">
                {topics[category]}
            </Typography>
        </Stack>
        <Grid container spacing={4}>
            {posts.map(post => (
                <Grid item xs={12} md={6} key={post.id}>
                    <PostCard
                        post={post}
                        onSelectPost={onSelectPost}
                        onToggleLike={onToggleLike}
                    />
                </Grid>
            ))}
        </Grid>
    </Box>
);

const HomePage = ({ posts, onSelectPost, onToggleLike, onViewCategory }) => (
    <Stack spacing={4}>
        {Object.entries(topics).map(([key, title]) => (
            <TopicSection
                key={key}
                title={title}
                posts={posts[key] || []}
                onSelectPost={onSelectPost}
                onToggleLike={onToggleLike}
                onViewMore={() => onViewCategory(key)}
            />
        ))}
    </Stack>
);

const PostDetail = ({ post, onBack, onToggleLike }) => (
    <Paper elevation={0}>
        <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ mb: 3 }}
        >
            戻る
        </Button>
        <CardMedia
            component="img"
            height={400}
            image={post.coverImage}
            alt={post.title}
            sx={{ borderRadius: 2, mb: 4 }}
        />
        <Stack spacing={4}>
            <Stack direction="row" spacing={2}>
                <Avatar
                    src={post.author.avatar}
                    alt={post.author.name}
                    sx={{ width: 56, height: 56 }}
                />
                <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                        {post.author.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {post.createdAt}
                    </Typography>
                </Box>
            </Stack>
            <Typography variant="h4" component="h1" fontWeight="bold">
                {post.title}
            </Typography>
            <Typography variant="body1">
                {post.content}
            </Typography>
            <Stack
                direction="row"
                spacing={2}
                sx={{ borderTop: 1, borderColor: 'divider', pt: 3 }}
            >
                <IconButton onClick={() => onToggleLike(post.id)}>
                    {post.isLiked ? (
                        <FavoriteIcon color="error" />
                    ) : (
                        <FavoriteBorderIcon />
                    )}
                </IconButton>
                <IconButton>
                    <CommentIcon />
                </IconButton>
                <IconButton>
                    <ShareIcon />
                </IconButton>
            </Stack>
        </Stack>
    </Paper>
);

const Note = () => {
    const [allPosts, setAllPosts] = useState(initialPosts);
    const [currentPost, setCurrentPost] = useState(null);
    const [currentCategory, setCurrentCategory] = useState(null);
    const theme = useTheme();

    const handleToggleLike = (postId) => {
        setAllPosts(
            Object.fromEntries(
                Object.entries(allPosts).map(([category, posts]) => [
                    category,
                    posts.map(post => {
                        if (post.id === postId) {
                            return {
                                ...post,
                                likes: post.isLiked ? post.likes - 1 : post.likes + 1,
                                isLiked: !post.isLiked
                            };
                        }
                        return post;
                    })
                ])
            )
        );
    };

    const handleSelectPost = (post) => {
        setCurrentPost(post);
        window.history.pushState(null, '', `/posts/${post.id}`);
    };

    const handleBack = () => {
        setCurrentPost(null);
        setCurrentCategory(null);
        window.history.pushState(null, '', '/');
    };

    const handleViewCategory = (category) => {
        setCurrentCategory(category);
        window.history.pushState(null, '', `/categories/${category}`);
    };

    return (
        <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh' }}>
            <SideMenuNote />
            <AppBar position="sticky" color="inherit" elevation={1}
             sx={{ 
                zIndex: theme.zIndex.appBar,
                marginLeft: 0,
                width: '100%'
              }}
            >
                <Toolbar>
                    <Container maxWidth="lg">
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                        >
                            <Typography
                                variant="h6"
                                component="h1"
                                sx={{ 
                                    cursor: 'pointer',
                                    marginLeft: { xs: '48px', sm: '48px' } // メニューボタンの幅を考慮
                                  }}
                                onClick={handleBack}
                            >
                                ourt Note
                            </Typography>
                            <Button
                                startIcon={<EditIcon />}
                                variant="contained"
                                color="primary"
                                onClick={() => navigate('/create')}
                            >
                                投稿する
                            </Button>
                        </Stack>
                    </Container>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg" sx={{ py: 4 }}>
                {currentPost ? (
                    <PostDetail
                        post={currentPost}
                        onBack={handleBack}
                        onToggleLike={handleToggleLike}
                    />
                ) : currentCategory ? (
                    <CategoryPage
                        category={currentCategory}
                        posts={allPosts[currentCategory]}
                        onSelectPost={handleSelectPost}
                        onToggleLike={handleToggleLike}
                        onBack={handleBack}
                    />
                ) : (
                    <HomePage
                        posts={allPosts}
                        onSelectPost={handleSelectPost}
                        onToggleLike={handleToggleLike}
                        onViewCategory={handleViewCategory}
                    />
                )}
            </Container>
        </Box>
    );
};

export default Note;